/**
 * Organizational Library client — mirrors
 * `apps/api/src/domains/knowledge/doctrine/routes.ts` verbatim.
 *
 * THIS IS THE HALF OF `knowledge` THAT IS NOT ARTICLES. `knowledge.ts` is the
 * Hub: articles a person writes, versions, attachments, acknowledgements. This
 * is the ingestion pipeline: documents an organization uploads, the readings
 * taken from them, the human approval that turns a reading into a rule, and the
 * resolver that says which rule governs a situation. They share a permission
 * prefix and nothing else.
 *
 * Permission split (checked at the route boundary, never here):
 *
 *   knowledge:read     list and open sources and rules; search; resolve
 *                      applicable rules; read the Library overview
 *   knowledge:write    upload a source; classify it; propose an extraction
 *   knowledge:publish  READ THE REVIEW QUEUE, and approve or reject
 *
 * The queue is deliberately on the higher key. It used to read
 * `knowledge:read`, which is seeded to `worker` — so a plain worker could read
 * the `proposedStatement` of every extraction awaiting review: text nobody has
 * approved, that is not the organization's policy, and that may be withdrawn
 * precisely because it is wrong.
 *
 * ---------------------------------------------------------------------------
 * FOUR THINGS A SCREEN MUST NOT RE-DERIVE
 * ---------------------------------------------------------------------------
 *
 * 1. A SOURCE IS NOT A RULE. Uploading a document activates no policy. The
 *    document becomes searchable immediately and is INERT until a human
 *    approves an extraction taken from it — enforced by a database trigger, not
 *    by this client and not by the service. {@link uploadSource} returns a
 *    source and deliberately says nothing about rules.
 *
 * 2. UPLOADED TEXT IS EVIDENCE, NEVER INSTRUCTION. {@link SourceView} carries
 *    `instructionShaped` and `instructionSignals` — a REVIEWER HINT saying the
 *    document contains text addressed to a machine, which is a strong sign it
 *    was tampered with. It is not a security control and must not be rendered
 *    as one: nothing downstream was ever going to obey the text either way, and
 *    the source is stored, searchable and shown to the reviewer regardless.
 *    Surface it as "this document talks to a computer, have a look", never as
 *    "blocked".
 *
 * 3. THE ANSWER DEPENDS ON WHEN YOU ASK. {@link resolveApplicable} takes an
 *    `at`, and a rule that was in force in June and superseded in August is the
 *    answer to a June question. A screen that omits `at` is asking about today
 *    and should say so; it must not present today's answer as the answer to a
 *    historical question.
 *
 * 4. WITHHELD IS NOT ABSENT, AND REFUSED IS NOT ZERO. Three shapes carry this,
 *    and all three are notation (B) of `docs/withholding-contract.md` — a
 *    nulled fragment with one flag beside it:
 *
 *      {@link LibraryRule.sourceTitle}     null iff `sourceWithheld`
 *      {@link RejectedRule.provenance}     null iff `provenanceWithheld`
 *      {@link LibraryOverview.pendingApprovals}
 *                                          null iff `pendingApprovalsRefused`
 *
 *    The last one is the one that bites. "There is nothing awaiting review" and
 *    "you may not ask" are different answers, and a screen that renders the
 *    second as `0` tells a reviewer their queue is clear when they simply
 *    cannot see it. Use {@link withheld.fromFlag} for the first two and check
 *    the flag for the third.
 */
import { get, post } from './transport.js';
export async function sources(query = {}, options) {
    const body = await get('/knowledge/sources', {
        ...options,
        query: { ...query },
    });
    return body.sources;
}
/**
 * One document, with everything drawn from it.
 *
 * 404 — NOT 403 — when the source is outside the caller's reading scope. A 403
 * would confirm the document is real, which is the fact being withheld: in this
 * product a document's title names a program, a payer, a service line or a
 * participant. The same choice the article half makes one route away.
 */
export function source(id, options) {
    return get(`/knowledge/sources/${id}`, options);
}
/**
 * Upload a document. THIS ACTIVATES NO POLICY — see the header, point 1.
 *
 * The response carries the source and says nothing about rules, deliberately,
 * so a screen cannot render "uploaded" as "in force".
 */
export async function uploadSource(input, options) {
    const body = await post('/knowledge/sources', input, options);
    return body.source;
}
/** Attach a human classification. Touches that one field; the text is frozen. */
export async function classifySource(id, classification, options) {
    const body = await post(`/knowledge/sources/${id}/classification`, { classification }, options);
    return body.source;
}
/**
 * Full-text search over the documents this caller may read.
 *
 * Returns {@link Evidence}, not rows, and the type is the point: whatever the
 * handbook says, what comes back is untrusted reference data carrying its
 * provenance, and there is no field on it through which a document could
 * influence what the reader is allowed to do.
 */
export async function searchSources(q, limit, options) {
    const body = await get('/knowledge/sources/search', {
        ...options,
        query: { q, ...(limit === undefined ? {} : { limit }) },
    });
    return body.results;
}
export async function proposeExtraction(input, options) {
    const body = await post('/knowledge/extractions', input, options);
    return body.extraction;
}
/**
 * Ask Phibian to read the whole document and draft readings. `knowledge:write`.
 * Each draft goes to `reviewerRoleId`'s review queue and is inert until approved.
 * Refusals carry a `code`: 503 `intelligence_provider_unconfigured`, 403
 * `intelligence_transmission_denied`, 409 `drafts_pending`, 422
 * `source_too_large_to_draft`, 429 `draft_rate_limited`, 502 `draft_model_failed`.
 */
export async function draftReadings(sourceId, input, options) {
    return post(`/knowledge/sources/${sourceId}/drafts`, input, options);
}
/** The review queue. `knowledge:publish` — see the header. 403 for everyone else. */
export async function pendingExtractions(options) {
    const body = await get('/knowledge/extractions/pending', options);
    return body.extractions;
}
export function approveExtraction(id, input, options) {
    return post(`/knowledge/extractions/${id}/approve`, input, options);
}
/** Reject an extraction. The reason is REQUIRED: a refusal nobody explained is not a decision. */
export async function rejectExtraction(id, note, options) {
    const body = await post(`/knowledge/extractions/${id}/reject`, { note }, options);
    return body.extraction;
}
export async function rules(query = {}, options) {
    const body = await get('/knowledge/rules', {
        ...options,
        query: { ...query },
    });
    return body.rules;
}
export function rule(id, options) {
    return get(`/knowledge/rules/${id}`, options);
}
export function ruleDependents(id, options) {
    return get(`/knowledge/rules/${id}/dependents`, options);
}
export function overview(options) {
    return get('/knowledge/library/overview', options);
}
/**
 * THE QUERY STRING IS BUILT HERE RATHER THAN HANDED TO `options.query`, AND
 * THAT IS NOT A STYLE CHOICE.
 *
 * `resolveQuerySchema` accepts `subject` as a string OR an array, and Fastify
 * produces an array from a REPEATED KEY (`?subject=a&subject=b`). The
 * transport's `QueryParams` is `Record<string, scalar>` and its serialiser
 * `append`s each entry once, so a repeated key cannot be expressed through it.
 *
 * The tempting shortcut — joining with a comma — is silently wrong: the server
 * would receive ONE subject literally named `a,b`, match nothing, and answer
 * with an empty resolution. That is a false "no rules apply", which on this
 * surface is a compliance answer somebody acts on. So the several-subject case
 * builds its own string, and the one-subject case goes through the same code so
 * there is only one path to be wrong.
 */
export function resolveApplicable(query = {}, options) {
    const { subject, ...rest } = query;
    const usp = new URLSearchParams();
    for (const [key, value] of Object.entries(rest)) {
        if (value === undefined || value === null)
            continue;
        usp.append(key, String(value));
    }
    for (const one of subject === undefined ? [] : [subject].flat()) {
        usp.append('subject', one);
    }
    const qs = usp.toString();
    return get(`/knowledge/applicable${qs ? `?${qs}` : ''}`, options);
}
//# sourceMappingURL=library.js.map